// The flat-row editing surface: one setting per line, label left, a control filling a fixed-width
// column on the right. Extracted from the Storefront Styles panel so the section editor's Appearance
// tab can render the same visual language over RAW CSS dictionaries (see Sections/SectionCssRows).
//
// The two surfaces differ ONLY in what the rows read and write:
//   * the Styles panel edits ISearchElementStyle parts   (`font`, numeric percent `fontSize`, …)
//   * the section editor edits raw CSS StyleProps        (`fontFamily`, `'1.2em'` fontSize, …)
// so the value-shaped pieces (ElementPropertyRows in styleControls / SectionCssRows) live with
// their conventions, and only the chrome — cell, row, colour row, group label, text-align group —
// is shared from here. (ElementPropertyRows sits in styleControls, not here, because styleControls
// already imports this file — the reverse import would cycle.)
//
// Column width is per-surface (the Styles panel is 340px, the section panel 460px), supplied
// through StyleRowsWidthProvider rather than threaded prop-by-prop.

import React from 'react';
import { Icon, IconButton, mergeStyles, useTheme, IDropdownStyles, ITextFieldStyles } from '@fluentui/react';
import StorefrontColorPickerButton from './StorefrontColorPickerButton';
import { PALETTE_SLOT_LABELS, paletteSlotOfToken } from './stylesConfig';

// The row's control shows the raw value as its caption. A `$palette:<slot>` token is not something
// to read as a colour, so it shows the slot's friendly name instead ("Accent" rather than
// "$palette:accent"); the chip beside it already paints the resolved colour.
const paletteRowLabel = (value?: string): string => {
    const slot = paletteSlotOfToken(value);
    if (!slot) return value || 'Default';
    // Keep an `@alpha` suffix visible - "Surface @0.55" is a materially different value.
    const at = (value as string).indexOf('@');
    return at >= 0 ? `${PALETTE_SLOT_LABELS[slot]} ${(value as string).slice(at)}` : PALETTE_SLOT_LABELS[slot];
};

// ── Flat-row editing surface ─────────────────────────────────────────────────────────────
// One property per row: label on the left, a compact control on the right. Everything writes into
// the same part dict as the Advanced editor, so the two can be mixed freely.

// Every flat row shares ONE fixed control column so all the controls line up.
//
// Sizing each Fluent control individually does NOT get there: the visible box is drawn by a
// different inner element per control type — TextField paints `fieldGroup`, Dropdown paints `title`
// (inset from its own root by the caret gutter), and the color picker paints the <button> itself —
// so `styles={{ root: { width: '100%' } }}` lines up the left edges but leaves the right edges
// ragged. Rather than chase each control's internals, ControlCell OWNS the chrome: it draws the
// underline at an exact size, and every control inside is stripped to a transparent, borderless,
// 100%-filling interior. The outer box is then literally the same <div> for every row, so alignment
// is exact by construction regardless of what sits inside.
//
// The chrome is Fluent's *underlined* treatment rather than a full box: a single hairline under the
// control, transparent background, no radius — much quieter down a long column of rows.
export const DEFAULT_CONTROL_WIDTH = 180;
export const CONTROL_HEIGHT = 32;
const CONTROL_LINE_HEIGHT = `${CONTROL_HEIGHT - 2}px`;

/** Per-surface control-column width. Wrap a surface to widen its rows. */
const ControlWidthContext = React.createContext<number>(DEFAULT_CONTROL_WIDTH);

export const StyleRowsWidthProvider: React.FC<{ width: number }> = ({ width, children }) => (
    <ControlWidthContext.Provider value={width}>{children}</ControlWidthContext.Provider>
);

export const useControlWidth = (): number => React.useContext(ControlWidthContext);

const useControlCellClass = (allowOverflow?: boolean): string => {
    const theme = useTheme();
    const width = useControlWidth();
    return React.useMemo(() => mergeStyles({
        width,
        height: CONTROL_HEIGHT,
        // border-box + a fixed height means thickening the underline on focus eats into the content
        // box rather than the outer box — the row height never changes, so nothing jumps.
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'stretch',
        // Clipped by default so nothing escapes the cell's box. Fluent's own flyouts (Dropdown,
        // ColorPicker) render in a Layer and are unaffected either way; a control that draws its
        // menu as a plain child instead (react-autosuggest) needs the escape hatch or its
        // suggestion list is cut off at 32px.
        overflow: allowOverflow ? 'visible' : 'hidden',
        background: 'transparent',
        // Resting rule is deliberately faint (neutralTertiaryAlt) so a column of ~10 rows reads as
        // quiet structure rather than a stack of boxes; hover and focus escalate from there.
        borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
        selectors: {
            ':hover': { borderBottomColor: theme.palette.neutralSecondary },
            // The inner controls' own borders and focus pseudo-elements are suppressed below, so
            // the cell carries the whole affordance.
            ':focus-within': {
                borderBottomWidth: 2,
                borderBottomColor: theme.palette.themePrimary,
            },
        },
    }), [theme, width, allowOverflow]);
};

export const ControlCell: React.FC<{ allowOverflow?: boolean }> = ({ allowOverflow, children }) =>
    <div className={useControlCellClass(allowOverflow)}>{children}</div>;

// Interior styles — no border, no background, no focus pseudo-element; just fill the cell.
// Theme-dependent (the unit suffix picks up a neutral), so built once per theme in a hook.
export const useCellControlStyles = () => {
    const theme = useTheme();
    return React.useMemo(() => ({
        textField: {
            root: { width: '100%', height: '100%' },
            wrapper: { height: '100%' },
            fieldGroup: {
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                selectors: { '::after': { display: 'none' } },
            },
            field: { height: '100%', lineHeight: CONTROL_LINE_HEIGHT, fontSize: 13, padding: '0 12px' },
            // Fluent's stock suffix is a neutralLighter block, which reads as a second control in an
            // underlined cell. Quiet it down to a plain unit hint sitting inside the field.
            suffix: {
                background: 'transparent',
                color: theme.palette.neutralSecondary,
                fontSize: 12,
                padding: '0 12px 0 0',
            },
        } as Partial<ITextFieldStyles>,
        dropdown: {
            root: { width: '100%', height: '100%' },
            dropdown: {
                width: '100%',
                height: '100%',
                selectors: {
                    ':focus::after': { display: 'none' },
                    ':focus .ms-Dropdown-title': { borderColor: 'transparent' },
                },
            },
            title: {
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                lineHeight: CONTROL_LINE_HEIGHT,
                fontSize: 13,
            },
            caretDownWrapper: { height: '100%', lineHeight: CONTROL_LINE_HEIGHT },
        } as Partial<IDropdownStyles>,
    }), [theme]);
};


export const StyleRow: React.FC<{
    label: string;
    children: React.ReactNode;
    /** See ControlCell: for a control whose menu is a plain child rather than a Layer. */
    allowOverflow?: boolean;
}> = ({ label, children, allowOverflow }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5em', minHeight: 36 }}>
        <span style={{ fontSize: 13, flex: '1 1 auto' }}>{label}</span>
        <ControlCell allowOverflow={allowOverflow}>{children}</ControlCell>
    </div>
);

// A color row: the shared swatch button opens the full picker (with "Unset (inherit)"). Its stock
// look is a small floating pill revealed on canvas hover, so it's stripped here to a transparent,
// borderless interior — ControlCell supplies the visible box, exactly as it does for the dropdowns
// and text fields above and below it.
export const ColorRow: React.FC<{
    label: string;
    value?: string;
    onChange: (value?: string) => void;
    /**
     * Show the picker's alpha slider, so the value comes back as rgba(). Off by default because
     * almost every storefront color is a solid fill; the exception is a color deliberately layered
     * over something else (the hero's floating header bar tint), where the transparency IS the point.
     */
    alpha?: boolean;
    /**
     * Offer the active Color Palette's slots (stored as a `$palette:<slot>` token). OPT-IN: only
     * the surfaces whose dict passes through the token resolvers switch it on - the section
     * editor's raw-CSS rows deliberately do not. See IStyleEntry.allowPaletteTokens.
     */
    allowPaletteTokens?: boolean;
}> = ({ label, value, onChange, alpha, allowPaletteTokens }) => (
    <StyleRow label={label}>
        <StorefrontColorPickerButton
            label={paletteRowLabel(value)}
            entries={[{ label, type: 'color', alpha, value, allowPaletteTokens, onChange: (v) => onChange(v), onClear: () => onChange(undefined) }]}
            style={{
                opacity: 1,
                position: 'static',
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                background: 'transparent',
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
                backdropFilter: 'none',
                fontSize: 13,
                padding: '0 12px',
            }}
        />
    </StyleRow>
);

// ── 2. Text align: a word-processor style toggle group filling the same cell ──────────────
export const TEXT_ALIGN_BUTTONS: { key: string; icon: string; title: string }[] = [
    { key: 'left', icon: 'AlignLeft', title: 'Left' },
    { key: 'center', icon: 'AlignCenter', title: 'Center' },
    { key: 'right', icon: 'AlignRight', title: 'Right' },
    { key: 'justify', icon: 'AlignJustify', title: 'Justify' },
];

export const TextAlignRow: React.FC<{ value?: string; onChange: (value?: string) => void }> = ({ value, onChange }) => {
    const theme = useTheme();
    return (
        <StyleRow label='Text align'>
            {TEXT_ALIGN_BUTTONS.map((option) => {
                const checked = value === option.key;
                return (
                    <IconButton
                        key={option.key}
                        iconProps={{ iconName: option.icon }}
                        // Clicking the active button clears the property back to Default (unset) —
                        // the same "click again to unset" behaviour the color rows have.
                        title={checked ? `${option.title} (click to clear)` : option.title}
                        ariaLabel={`Align ${option.title.toLowerCase()}`}
                        checked={checked}
                        onClick={() => onChange(checked ? undefined : option.key)}
                        styles={{
                            root: { flex: '1 1 0', minWidth: 0, height: '100%', background: 'transparent', border: 'none' },
                            rootHovered: { background: theme.palette.neutralLighter },
                            rootPressed: { background: theme.palette.neutralLight },
                            rootChecked: { background: theme.palette.themeLighter },
                            rootCheckedHovered: { background: theme.palette.themeLight },
                            icon: { fontSize: 14, color: checked ? theme.palette.themePrimary : theme.palette.neutralPrimary },
                            iconHovered: { color: checked ? theme.palette.themePrimary : theme.palette.neutralPrimary },
                            iconChecked: { color: theme.palette.themePrimary },
                        }}
                    />
                );
            })}
        </StyleRow>
    );
};

// Subordinate heading inside an already-open part group. Same typography as the part labels but
// dimmer and smaller — these are plain labels, deliberately NOT nested accordions.
export const RowGroupLabel: React.FC<{ text: string; iconName?: string }> = ({ text, iconName }) => {
    const theme = useTheme();
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            color: theme.palette.neutralTertiary, marginTop: '0.75em', marginBottom: 2,
            borderTop: `1px solid ${theme.palette.neutralLight}`, paddingTop: '0.75em',
        }}>
            {/* Deliberately quieter than the part-group icons: 11px and neutralTertiary, inheriting
                the label's own colour so these read as subordinate headings inside an open group. */}
            {iconName && <Icon iconName={iconName} style={{ fontSize: 11 }} />}
            {text}
        </div>
    );
};
